import { useCallback, useEffect, useState } from "react";

/**
 * Light or dark. Follows the device until someone chooses, then remembers the choice in this
 * browser. public/theme-init.js applies a saved choice before the first paint.
 */
export type Theme = "light" | "dark";

const STORAGE_KEY = "sd-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
const CHANGED = "sd-theme-change"; // keeps every theme control on the page in step

function savedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

function deviceTheme(): Theme {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

export function useTheme(): { theme: Theme; setTheme: (theme: Theme) => void } {
  const [chosen, setChosen] = useState<Theme | null>(savedTheme);
  const [device, setDevice] = useState<Theme>(deviceTheme);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(DARK_QUERY);
    const update = () => setDevice(query.matches ? "dark" : "light");
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const sync = (event: Event) => setChosen((event as CustomEvent<Theme>).detail);
    window.addEventListener(CHANGED, sync);
    return () => window.removeEventListener(CHANGED, sync);
  }, []);

  const setTheme = useCallback((theme: Theme) => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Not remembered, but still applied for this visit.
    }
    setChosen(theme);
    window.dispatchEvent(new CustomEvent(CHANGED, { detail: theme }));
  }, []);

  return { theme: chosen ?? device, setTheme };
}
